import React, { ChangeEvent, useState } from "react";
import { Button, Drawer, Flex, Form, Input, Typography, useMessagedAsyncFn } from "@payping/web-components";
import { CheckCircleOutlined, Close } from "@payping/icons";
import { useNavigate } from "react-router-dom";
import { accountApis } from "@payping/api";
import { OtpCode } from "@/components/OtpCode";
import { DashboardRoutes } from "@/router/constant";
import { useAppTranslation } from "@/utils";

export interface PasswordFormType {
  newPassword: string;
  confirmPassword: string;
  otpCode: string;
}
const MobileProfilePassword: React.FC = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm<PasswordFormType>();
  const [isOtpValid, setIsOtpValid] = useState(false);

  const [password, setPassword] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState({
    hasNumber: false,
    hasLetter: false,
    hasUpperCase: false,
    minLength: false,
  });

  const validatePassword = (value: string) => {
    const hasNumber = /\d/.test(value); // شامل عدد
    const hasLetter = /[a-zA-Z]/.test(value); // شامل حروف
    const hasUpperCase = /[A-Z]/.test(value); // حداقل یک حرف بزرگ
    const minLength = value.length >= 6; // حداقل طول

    setIsPasswordValid({ hasNumber, hasLetter, hasUpperCase, minLength });
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPassword(value);
    validatePassword(value);
  };

  const [{ loading }, handleEdit] = useMessagedAsyncFn(
    async (values: PasswordFormType) => {
      try {
        await accountApis.changePasswordAsyncWithRequestModelPut(values);
        navigate(DashboardRoutes.profile.mRoot.url);
      } catch (e) {
        // eslint-disable-next-line no-console
      }
    },
    [],
    undefined,
    { useProgress: true },
  );
  return (
    <>
      <Drawer
        title={t("profile.changePassword")}
        open
        height={"100%"}
        placement={"bottom"}
        closeIcon={false}
        extra={
          <Button
            type={"text"}
            icon={<Close width={14} height={14} />}
            onClick={() => {
              navigate(DashboardRoutes.profile.mRoot.url);
            }}
          />
        }
        footer={
          <Flex gap={"small"} className="tw-flex tw-justify-end">
            <Button type={"default"} onClick={() => navigate(DashboardRoutes.profile.mRoot.url)}>
              {t("return")}
            </Button>
            <Button onClick={() => form.submit()} type={"primary"} disabled={!isOtpValid} loading={loading}>
              {t("save")}
            </Button>
          </Flex>
        }
      >
        <Form<PasswordFormType> onFinish={handleEdit} form={form as any} layout={"vertical"}>
          <Form.Item name={"newPassword"} label={t("profile.pass")} rules={[{ required: true }]}>
            <Input.Password
              size={"large"}
              className="en-font"
              direction={"ltr"}
              value={password}
              placeholder={t("profile.changePassPlaceHolder")}
              onChange={handlePasswordChange}
            />
          </Form.Item>
          <Form.Item name={"confirmPassword"} label={t("settings.changePassword.repeat")} rules={[{ required: true }]}>
            <Input.Password
              size={"large"}
              className="en-font"
              direction={"ltr"}
              placeholder={t("profile.changePassPlaceHolder")}
            />
          </Form.Item>
          <Flex vertical gap={"large"}>
            <Flex vertical gap={"small"}>
              <Flex gap={"small"}>
                {isPasswordValid.hasLetter && isPasswordValid.hasNumber ? (
                  <CheckCircleOutlined color={"green"} width={16} />
                ) : (
                  <CheckCircleOutlined color={"gray"} width={16} />
                )}

                <Typography.Text
                  type={isPasswordValid.hasLetter && isPasswordValid.hasNumber ? "success" : "secondary"}
                >
                  {t("profile.containsLettersAndNumbers")}
                </Typography.Text>
              </Flex>
              <Flex gap={"small"}>
                {isPasswordValid.minLength ? (
                  <CheckCircleOutlined color={"green"} width={16} />
                ) : (
                  <CheckCircleOutlined color={"gray"} width={16} />
                )}
                <Typography.Text type={isPasswordValid.minLength ? "success" : "secondary"}>
                  {t("profile.atLeast6Letters")}
                </Typography.Text>
              </Flex>
              <Flex gap={"small"}>
                {isPasswordValid.hasUpperCase ? (
                  <CheckCircleOutlined color={"green"} width={16} />
                ) : (
                  <CheckCircleOutlined color={"gray"} width={16} />
                )}
                <Typography.Text type={isPasswordValid.hasUpperCase ? "success" : "secondary"}>
                  {t("profile.atLeastOneCapitalEnglishLetter")}
                </Typography.Text>
              </Flex>
            </Flex>
            <OtpCode form={form} onVerify={(isVerified) => setIsOtpValid(isVerified)} />
          </Flex>
        </Form>
      </Drawer>
    </>
  );
};

export default MobileProfilePassword;
